// Designer host for the shared end-user transaction screen
// (@twasta/page-runtime/txn/screen TxnScreen) inside the transaction
// designer's Live Preview tab (and the /run view, which reuses this
// component). The whole end-user experience — list, editor, actions,
// approvals badge, history, deep links — now lives in the package; this
// wrapper owns only the DESIGNER chrome around it:
//
//   • deploy lifecycle (deploy-on-mount via runtime.ensureReady, redeploy,
//     status/menu, function-compile failures + fix-code)
//   • reset preview data (type-to-confirm lives in DeployMenu)
//   • Customize mode (per-tenant screen customization, entered from the ⋮) + preview audit/log modals
//   • the capture interfaces (spreadsheet / form-input) as slots — Phase 3b
//     keeps EmbedCaptureTabularPreview / EmbedCapturePreview host-side
//   • the ApprovalsInbox + ErrorDetailsModal chrome as slots

import type { MoreMenuItem } from '@twasta/page-runtime/customize'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { TransactionModel } from '@/pages/designers/transaction/types'
import {
  TxnScreen, rememberView, ObjectDrawer,
  type TxnScreenHandle, type TxnScreenInterfaceContext,
} from '@twasta/page-runtime/txn/screen'
import { openScreenPayloadOf } from '@twasta/page-runtime/txn'
import type {
  OpenActionRequest, OpenScreenPayload, SplitScrollHandle, WizardActionRequest,
} from '@twasta/page-runtime/txn'
import OpenObjectPane from './runtime/OpenObjectPane'
import { PlatformWizard } from './runtime/wizardHost'
import type { EmbedListView } from '@twasta/page-runtime/txn/list'
import {
  livePreviewDeploy, livePreviewReset, modelOverlayCanCustomize,
  worklistCreate, worklistPrincipals,
} from './api'
import { AssignTaskDialog } from '@twasta/page-runtime/shell'
import type { FunctionDeployResult, SyncReport } from './types'
import { parseApiError } from './utils'
import DeployMenu from './runtime/DeployMenu'
import ErrorDetailsModal from './runtime/ErrorDetailsModal'
import FunctionErrorModal from './runtime/FunctionErrorModal'
import PreviewAuditModal from './runtime/PreviewAuditModal'
import PreviewLogModal from './runtime/PreviewLogModal'
import ApprovalsInbox from './runtime/ApprovalsInbox'
import { makeDesignerScreenRuntime } from './runtime/designerScreenRuntime'
import EmbedCaptureTabularPreview from '@/pages/designers/page/EmbedCaptureTabularPreview'
import EmbedCapturePreview from '@/pages/designers/page/EmbedCapturePreview'
import type { EmbedConfig } from '@/pages/designers/page/types'
import { canEditObjectsFor, useCanLivePreview } from '@/auth/useProjectPermissions'
import { useNavigate } from 'react-router-dom'
import SaveVersionDialog, { type SaveVersionObject } from '@/components/workbench/SaveVersionDialog'
import { prefillDock } from '@/store/dockBridgeStore'
import { useJourneyStore } from '@/store/journeyStore'
import { useDocumentInterface } from './runtime/useDocumentInterface'

interface Props {
  project: string
  objName: string
  model: TransactionModel
  /** Run-view deep linking: open a record on mount (mode 'add' | 'edit' + rec).
   *  Only set when the host (RunPage) drives the URL. */
  initialScreen?: { mode?: string; rec?: string; view?: string
    seed?: Record<string, unknown>
    /** A handler='open' action button opened this screen (see TxnScreen). */
    open?: OpenScreenPayload; openKey?: string }
  /** Reflect the open record back to the URL so it can be shared. */
  onScreenChange?: (s: { mode?: string; rec?: string } | null) => void
  /** Where "Apply layout" persists width/order: 'design' (default) writes the
   *  shared transaction JSON — the designer live preview; 'customize' stores a
   *  per-tenant overlay — the run app, leaving the core design untouched. */
  layoutMode?: 'design' | 'customize'
  /**
   * Offer "Assign to…" on a record: hand THIS record to a colleague or a
   * role, and it lands in their work list opened on it. True only where the
   * surface actually mounts a work panel (the Run app) — in the designer's
   * live preview the task would be real but invisible, which is worse than
   * not offering it.
   */
  canAssignWork?: boolean
  /**
   * Open a governed document's filed copy in its repository — offered by the
   * Document view of a record. Absent in the designer's live preview, which
   * has no other object to navigate to, and the link is then not drawn.
   */
  onOpenFiled?: (args: { repositoryObj: string; documentId: string }) => void
  /**
   * A handler='open' action button asked to NAVIGATE to another object. The
   * Run shell owns navigation and supplies this; without it (the designer's
   * live preview) the target opens in a drawer over this screen instead.
   */
  onOpenObject?: (req: OpenActionRequest) => void
  /**
   * A handler='wizard' action button asked to NAVIGATE to its Transaction
   * Wizard. Supplied by the Run shell; without it (or for presentation
   * 'drawer') the wizard runs in a drawer over this screen.
   */
  onOpenWizard?: (req: WizardActionRequest) => void
  /** The record editor opened / closed (a drawer holding this screen closes
   *  with it). */
  onEditorChange?: (s: { open: boolean; txnId: string | null }) => void
  /** Make changes changed the model on disk — a host holding its own copy
   *  (the transaction designer) re-reads it. */
  onBuildChange?: () => void
  /** The host draws its own Make changes button (the Workbench header): each
   *  new number asks to open the mode; `onMakeChangesOpened` acknowledges it,
   *  so a remount never replays an old request. */
  makeChangesRequest?: number
  onMakeChangesOpened?: () => void
}

// Design tab layout → the screen view that draws the records that way.
const DESIGNED_VIEW: Record<string, EmbedListView | undefined> = {
  form: 'form-input',
  grid: 'list',
  feed: 'compact',
}

export default function LivePreviewTab({
  project, objName, model, initialScreen, onScreenChange, layoutMode = 'design',
  canAssignWork = false, onOpenFiled, onOpenObject, onOpenWizard, onEditorChange, onBuildChange,
  makeChangesRequest, onMakeChangesOpened,
}: Props) {
  // The view the header form was saved on in the designer's Design tab
  // (`view_layout`) is the view this screen opens on. In the designer preview
  // it is applied on every open, ahead of <TxnScreen>'s remembered view, so a
  // view picked here last time can't hide what Design was saved with. The Run
  // view keeps the end user's own last choice and uses it only as the default.
  const designedView = DESIGNED_VIEW[model.sql_models?.[0]?.sql_model?.view_layout || '']
  useState(() => {
    if (layoutMode === 'design' && designedView) rememberView(project, objName, designedView)
    return null
  })
  // The record the user asked to hand to somebody, with the label the rest of
  // the app shows for it (the screen resolves that — one record reads one way
  // everywhere).
  const [assignFor, setAssignFor] = useState<{
    objName: string; recordKey: string; label: string; objTitle: string
  } | null>(null)
  const assignRuntime = useMemo(() => ({
    create: (input: Record<string, unknown>) => worklistCreate(project, input),
    principals: (q?: string) => worklistPrincipals(project, q),
    notify: (message: string, kind?: 'success' | 'error' | 'info') => {
      if (kind === 'error') toast.error(message)
      else toast.success(message)
    },
  }), [project])
  // Imperative surface into the shared screen (refresh / clear editor /
  // report errors / busy overlay) for the designer-only flows below.
  const screenRef = useRef<TxnScreenHandle | null>(null)
  // Split-scroll handle shared with the screen's list/spreadsheet so the
  // menu's "Freeze first column" drives whichever view is mounted.
  const splitHandleRef = useRef<SplitScrollHandle | null>(null)
  // In the Run view ('customize' layout mode) only the organization's
  // functional administrator may save a column layout into its customization;
  // anyone else may still rearrange columns for the session.
  const [canCustomize, setCanCustomize] = useState(false)
  useEffect(() => {
    if (layoutMode !== 'customize') return undefined
    let cancelled = false
    modelOverlayCanCustomize(project, 'T').then((v) => { if (!cancelled) setCanCustomize(v) })
    return () => { cancelled = true }
  }, [project, layoutMode])

  // Customize mode updates the screen's own working model, so the host
  // simply passes its model through.
  const effectiveModel = model

  // ── Deploy state (designer-only) ─────────────────────────────────────
  const [deploying, setDeploying] = useState(false)
  const [deployError, setDeployError] = useState<string | null>(null)
  // A marketplace install that ended (or is still being set up) is refused by
  // the runtime gate with a plain message meant for the END USER — shown as
  // the screen itself, never as a developer error dialog.
  const [blocked, setBlocked] = useState<string | null>(null)
  const [schema, setSchema] = useState<string | null>(null)
  const [database, setDatabase] = useState<string | null>(null)
  const [sync, setSync] = useState<SyncReport | null>(null)
  const [functions, setFunctions] = useState<FunctionDeployResult[]>([])
  const [activeFailure, setActiveFailure] = useState<FunctionDeployResult | null>(null)
  // Bumping this re-runs the screen's ensureReady → deploy → reload sequence
  // (initial mount deploys once; "Redeploy" bumps it).
  const [deployNonce, setDeployNonce] = useState(0)

  const [auditOpen, setAuditOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)

  // Deploy the preview schema + functions. Called by the screen via
  // runtime.ensureReady (mount + redeploy). Throws on failure AFTER
  // surfacing it, so the screen stays un-ready instead of fetching.
  const deploy = useCallback(async () => {
    setDeploying(true)
    setDeployError(null)
    try {
      const res = await livePreviewDeploy(project, objName)
      setDatabase(res.database)
      setSchema(res.schema)
      setSync(res.sync)
      setFunctions(res.functions || [])
      const warns = res.sync.tables.reduce(
        (a, t) => a + t.warnings.length, 0,
      ) + res.sync.warnings.length
      const fnErrors = (res.functions || []).filter(f => f.status === 'error')
      if (fnErrors.length) {
        toast.error(`${fnErrors.length} function(s) failed to compile`)
      } else if (warns) {
        toast(`Deployed with ${warns} warning(s)`, { icon: '⚠️' })
      }
    } catch (err) {
      const { message, status } = parseApiError(err)
      // 403 = this caller may not deploy. That is the cross-tenant runtime
      // guest running the app (RUNTIME_USER holds project.run_app, never
      // project.live_preview): deploy is the AUTHOR's maintenance op — DDL +
      // business-logic compile — and the schema they are running against is
      // already deployed. So it is "nothing to do", not a failure: let the
      // screen become ready, exactly as EmbedLivePreview does in site mode.
      // Every other failure still blocks the screen.
      if (status === 403) return
      const detail = (err as { response?: { data?: { detail?: { code?: string; message?: string } } } })?.response?.data?.detail
      if (detail && typeof detail === 'object' && String(detail.code || '').startsWith('MARKETPLACE_')) {
        setBlocked(detail.message || message)
        return
      }
      setDeployError(message)
      screenRef.current?.reportError('Deploy failed', err)
      throw err
    } finally {
      setDeploying(false)
    }
  }, [project, objName])
  // ensureReady must be identity-stable inside the memoized runtime — route
  // it through a ref so redeploys always call the latest deploy closure.
  const deployRef = useRef(deploy)
  useEffect(() => { deployRef.current = deploy })

  // handler='open' action buttons: navigate when the host can (the Run
  // shell), otherwise — and whenever the button asks for it — open the target
  // in a drawer over this screen. Through a ref: the runtime is memoized.
  const [openDrawer, setOpenDrawer] = useState<{ req: OpenActionRequest; key: string } | null>(null)
  const drawerSeq = useRef(0)
  const onOpenObjectRef = useRef(onOpenObject)
  useEffect(() => { onOpenObjectRef.current = onOpenObject })
  const openObject = useCallback((req: OpenActionRequest) => {
    const navigate = onOpenObjectRef.current
    if (req.presentation === 'navigate' && navigate) {
      navigate(req)
      return
    }
    drawerSeq.current += 1
    setOpenDrawer({ req, key: `drawer-${drawerSeq.current}` })
  }, [])
  const openDrawerRef = useRef(openDrawer)
  openDrawerRef.current = openDrawer
  const closeDrawer = useCallback(() => {
    const cur = openDrawerRef.current
    if (!cur) return
    openDrawerRef.current = null
    setOpenDrawer(null)
    cur.req.onClosed?.()
  }, [])
  // A transaction drawer closes with the record editor it opened (see
  // ObjectDrawer): once the editor has been open, its closing ends the drawer.
  const drawerEditorSeen = useRef(false)
  useEffect(() => { drawerEditorSeen.current = false }, [openDrawer])
  const onDrawerEditorChange = useCallback((st: { open: boolean }) => {
    if (st.open) { drawerEditorSeen.current = true; return }
    if (drawerEditorSeen.current) closeDrawer()
  }, [closeDrawer])

  // handler='wizard' action buttons: the same split — navigate when the host
  // can and the button asks for it, otherwise run the wizard in a drawer.
  const [wizardDrawer, setWizardDrawer] = useState<{ req: WizardActionRequest; key: string } | null>(null)
  const onOpenWizardRef = useRef(onOpenWizard)
  useEffect(() => { onOpenWizardRef.current = onOpenWizard })
  const openWizard = useCallback((req: WizardActionRequest) => {
    const navigate = onOpenWizardRef.current
    if (req.presentation === 'navigate' && navigate) {
      navigate(req)
      return
    }
    drawerSeq.current += 1
    setWizardDrawer({ req, key: `wizard-${drawerSeq.current}` })
  }, [])
  const closeWizardDrawer = useCallback(() => {
    setWizardDrawer((cur) => {
      cur?.req.onClosed?.()
      return null
    })
  }, [])

  // ── Make changes (the screen itself; people who build the app) ───────
  // Offered on the builder's previews only — never the Run view, which is
  // the application as its users get it.
  const navigate = useNavigate()
  const offerBuild = layoutMode === 'design'
  const hostMakeChanges = makeChangesRequest !== undefined
  const [canBuild, setCanBuild] = useState(false)
  useEffect(() => {
    if (!offerBuild) { setCanBuild(false); return undefined }
    let cancelled = false
    canEditObjectsFor(project).then((v) => { if (!cancelled) setCanBuild(v) })
    return () => { cancelled = true }
  }, [project, offerBuild])
  const [saveVersionFor, setSaveVersionFor] = useState<(SaveVersionObject & { changes: string[] }) | null>(null)
  const buildHooks = useMemo(() => (offerBuild ? {
    // The AI building THIS screen right now: the mode opens read-only.
    readOnlyReason: (obj: string) => {
      const j = useJourneyStore.getState().journey
      const building = j?.agent.status === 'running' && (j.objects || []).some(
        (o) => o.obj_type === 'T' && o.obj_name.toLowerCase() === obj.toLowerCase() && o.status === 'building')
      return building ? 'the AI is building this screen right now. Changes open again when it finishes.' : null
    },
    saveVersion: (args: { objName: string; title: string; changes: string[] }) =>
      setSaveVersionFor({ objType: 'T', objName: args.objName, title: args.title, changes: args.changes }),
    openDesigner: (obj: string, section?: string) =>
      navigate(`/designers/transaction/${encodeURIComponent(project)}/${encodeURIComponent(obj)}${section ? `?section=${encodeURIComponent(section)}` : ''}`),
    // Pre-fills the Ask-AI dock (it never sends on its own).
    askAi: (text: string) => prefillDock(text),
    hostButton: hostMakeChanges,
  } : undefined), [offerBuild, project, navigate, hostMakeChanges])

  // The host's Make changes button: open the mode once the caller is known
  // to build the app (the screen is mounted by then — its handle is set in a
  // child layout effect, ahead of this one).
  const onMakeChangesOpenedRef = useRef(onMakeChangesOpened)
  onMakeChangesOpenedRef.current = onMakeChangesOpened
  useEffect(() => {
    if (!makeChangesRequest || !canBuild) return
    const screen = screenRef.current
    if (!screen) {
      console.error('[make-changes] the screen is not ready to open Make changes')
      return
    }
    screen.enterCustomize('app')
    onMakeChangesOpenedRef.current?.()
  }, [makeChangesRequest, canBuild])

  const runtime = useMemo(
    () => makeDesignerScreenRuntime(project, {
      ensureReady: () => deployRef.current(),
      openObject,
      openWizard,
      build: buildHooks,
    }),
    [project, openObject, openWizard, buildHooks],
  )

  // Clear the preview data tables. Confirmation (type-to-confirm) is owned
  // by DeployMenu's ResetConfirmModal.
  const reset = useCallback(async () => {
    const screen = screenRef.current
    screen?.setBusy(true)
    try {
      const res = await livePreviewReset(project, objName)
      toast.success(`Cleared ${res.truncated.length} table(s)`)
      screen?.closeAndClearEditor()
      await screen?.refresh()
    } catch (err) {
      screen?.reportError('Reset failed', err)
    } finally {
      screen?.setBusy(false)
    }
  }, [project, objName])

  // ── Designer chrome nodes handed to the screen ───────────────────────

  // The menu is AUTHOR chrome on a surface end users also use: Redeploy, Reset
  // preview data and the Preview log all require `project.live_preview`, which a
  // runtime-only member (a cross-tenant guest, or anyone the Team screen made a
  // RUNTIME_USER) does not hold. Offering it to them put the same "Missing
  // required project permission: project.live_preview" that used to block the
  // screen itself one click away, on a screen they had been granted. So it is
  // shown to the callers whose clicks the endpoints will actually answer.
  const canLivePreview = useCanLivePreview(project)
  // A functional administrator without the builder's menu gets the screen's
  // own ⋮ (TxnScreen renders it when headerActions is absent and the runtime
  // says they may customize).
  const deployMenuWith = (extraItems: MoreMenuItem[]) => (
    <DeployMenu
      extraItems={extraItems}
      schema={schema}
      database={database}
      sync={sync}
      loading={deploying}
      error={deployError}
      functions={functions}
      onRedeploy={() => setDeployNonce(n => n + 1)}
      onOpenFailure={setActiveFailure}
      onOpenAudit={() => setAuditOpen(true)}
      onOpenLog={() => setLogOpen(true)}
      onMakeChanges={canBuild ? () => screenRef.current?.enterCustomize('app') : undefined}
      onCustomize={() => screenRef.current?.enterCustomize()}
      onReset={reset}
      resetConfirmName={objName}
      onFreezeColumn={() => splitHandleRef.current?.freeze()}
    />
  )
  const deployMenu = canLivePreview !== true ? undefined : deployMenuWith([])

  // Minimal embed config so the shared smart-page capture interfaces can run
  // against this standalone transaction (no page inputs / filters).
  const syntheticEmbed = (view: TxnScreenInterfaceContext['view']): EmbedConfig => ({
    obj_name: objName,
    obj_type: 'T',
    mode: 'list',
    filters: [],
    renderMode: 'capture',
    captureTemplate: view === 'form-input' ? 'multi-form-input' : 'multi-tabular',
  })

  // The capture interfaces stay host-side for now (Phase 3b moves them);
  // the screen renders them as slots in the SAME filter/action context as
  // its list, threading the switcher / approvals / deploy menu through.
  const interfaces = {
    tabular: (ctx: TxnScreenInterfaceContext) => (
      <EmbedCaptureTabularPreview
        project={project}
        embed={syntheticEmbed(ctx.view)}
        inputs={[]}
        inputValues={{}}
        layoutMode={layoutMode}
        canAdd={ctx.canAdd}
        canEdit={ctx.canEdit}
        viewSwitcher={ctx.switcher}
        beforeAddActions={ctx.approvalsButton}
        headerActions={deployMenu}
        splitHandle={ctx.splitHandle}
        enforcedOverrides={ctx.enforcedOverrides}
        filterSetId={ctx.activeFilterSetId ?? undefined}
        listTab={ctx.activeTab}
        bypassEnforced={ctx.bypassed}
        screenChrome
        onApplyOverrides={ctx.applyFilters}
        onSelectFilterSet={ctx.selectFilterSet}
        rowActions={ctx.rowActions}
        onRunRowAction={ctx.runRowAction}
        bulkActions={ctx.bulkActions}
        onRunBulkAction={ctx.runBulkAction}
        bulkBusy={ctx.bulkBusy}
      />
    ),
    formInput: (ctx: TxnScreenInterfaceContext) => (
      <EmbedCapturePreview
        project={project}
        embed={syntheticEmbed(ctx.view)}
        inputs={[]}
        inputValues={{}}
        layoutMode={layoutMode}
        canAdd={ctx.canAdd}
        canEdit={ctx.canEdit}
        viewSwitcher={ctx.switcher}
        variant="bulk"
        listTab={ctx.activeTab}
        enforcedOverrides={ctx.enforcedOverrides}
        filterSetId={ctx.activeFilterSetId ?? undefined}
        bypassEnforced={ctx.bypassed}
        onApplyOverrides={ctx.applyFilters}
        onSelectFilterSet={ctx.selectFilterSet}
        editorLayout={ctx.bulkEditorLayout}
        editorLayoutSwitch={ctx.editorLayoutSwitch({ exclude: ['document'] })}
      />
    ),
  }

  // "Document" — the fourth view. Resolved per object: present only where a
  // Document Form (obj_type `E`) is anchored on this transaction, so the
  // switcher never offers a face this object does not have.
  const { documentView } = useDocumentInterface({
    project,
    objName,
    onSaved: () => { void screenRef.current?.refresh?.() },
    onOpenFiled,
  })

  const extraModals = (
    <>
      {/* Another object opened by a handler='open' action button. */}
      {openDrawer && (
        <ObjectDrawer
          title={openDrawer.req.title}
          objType={openDrawer.req.objType}
          openMode={openDrawer.req.mode}
          onClose={closeDrawer}
        >
          <OpenObjectPane
            project={project}
            payload={openScreenPayloadOf(openDrawer.req)}
            openKey={openDrawer.key}
            layoutMode={layoutMode}
            onEditorChange={onDrawerEditorChange}
          />
        </ObjectDrawer>
      )}
      {/* A Transaction Wizard opened by a handler='wizard' action button. */}
      {wizardDrawer && (
        <ObjectDrawer
          title={wizardDrawer.req.title}
          objType="U"
          onClose={closeWizardDrawer}
        >
          <PlatformWizard
            key={wizardDrawer.key}
            project={project}
            wizard={wizardDrawer.req.objName}
            inputs={wizardDrawer.req.inputs}
            presentation="drawer"
            onClose={closeWizardDrawer}
            seed={wizardDrawer.req.fill?.seed}
            onFill={wizardDrawer.req.fill?.onFill}
          />
        </ObjectDrawer>
      )}
      {/* Hand this record to a colleague or a role. Mounted here rather than
          beside the top bar's work panel: the row that opens it is on THIS
          screen, and a dialog that has to travel through a store to reach the
          click that opened it is a store nobody needed. */}
      {assignFor && (
        <AssignTaskDialog
          runtime={assignRuntime}
          record={assignFor}
          onClose={() => setAssignFor(null)}
        />
      )}
      <FunctionErrorModal
        open={!!activeFailure}
        project={project}
        objName={objName}
        failure={activeFailure}
        onClose={() => setActiveFailure(null)}
        onRedeployed={(result) => {
          setFunctions(prev => prev.map(
            f => f.name === result.name ? result : f,
          ))
        }}
      />

      {auditOpen && (
        <PreviewAuditModal
          project={project}
          defaultObjName={objName}
          onClose={() => setAuditOpen(false)}
        />
      )}

      <PreviewLogModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        projectName={project}
        objectName={objName}
      />

      {/* Make changes › More actions › Save a version (this screen). */}
      <SaveVersionDialog
        open={!!saveVersionFor}
        onClose={() => setSaveVersionFor(null)}
        project={project}
        object={saveVersionFor ?? undefined}
        changeLines={saveVersionFor?.changes}
      />

    </>
  )

  if (blocked) {
    return (
      <div role="alert" style={{ margin: '48px auto', maxWidth: 560, padding: 24, borderRadius: 12, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)', lineHeight: 1.5 }}>
        <strong style={{ display: 'block', fontSize: 17, marginBottom: 6 }}>This app isn't available right now</strong>
        <span style={{ color: 'var(--color-text-secondary)' }}>{blocked}</span>
      </div>
    )
  }

  return (
    <TxnScreen
      project={project}
      objName={objName}
      model={effectiveModel}
      runtime={runtime}
      gridLayoutMode={layoutMode}
      canSaveLayout={layoutMode !== 'customize' || canCustomize}
      busy={deploying}
      headerActions={deployMenu}
      // The list hands its own items (row density) to the same ⋮ menu.
      renderMore={canLivePreview === true ? deployMenuWith : undefined}
      onBuildChange={onBuildChange}
      extraModals={extraModals}
      interfaces={interfaces}
      defaultView={designedView}
      documentView={documentView}
      renderApprovalsInbox={({ onClose, onCountChange }) => (
        <ApprovalsInbox
          project={project}
          onClose={onClose}
          onCountChange={onCountChange}
        />
      )}
      renderErrorDetails={({ report, onClose }) => (
        <ErrorDetailsModal report={report} onClose={onClose} />
      )}
      initialScreen={initialScreen}
      onScreenChange={onScreenChange}
      onEditorChange={onEditorChange}
      versionKey={deployNonce}
      splitHandle={splitHandleRef}
      apiRef={screenRef}
      onAssignRecord={canAssignWork ? setAssignFor : undefined}
    />
  )
}
